import { useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import api, { formatApiError, formatMoney } from "@/lib/api";
import { CreditCard, CheckCircle, Fire } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { Link, Navigate, useSearchParams } from "react-router-dom";

export default function Billing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [params] = useSearchParams();
  const requestedLevel = Number(params.get("level")) || 1;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/plans")
      .then((r) => setPlans(r.data))
      .catch((err) => console.error("Failed to load plans:", err));
  }, []);

  const currentLevel = user?.level || 0;

  // Users who already hold at least the requested tier can't re-purchase that tier.
  const effectiveLevel = useMemo(() => {
    if (currentLevel === 0) return requestedLevel;
    if (requestedLevel > currentLevel) return requestedLevel;
    return null; // already at or above, nothing to buy
  }, [currentLevel, requestedLevel]);

  if (effectiveLevel === null) return <Navigate to="/account" replace />;

  const plan = plans.find((p) => p.level === effectiveLevel);
  const founding = plan?.founding_active;
  const price = founding ? plan?.founding_price : plan?.yearly_price;

  const checkout = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/payments/checkout", {
        origin_url: window.location.origin,
        plan_level: effectiveLevel,
      });
      window.location.href = data.url;
    } catch (err) {
      setError(formatApiError(err));
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 lg:px-8 py-16">
        <div className="text-center mb-12">
          <div className="section-label mb-3">— ACTIVATE MEMBERSHIP</div>
          <h1 className="font-chivo font-black text-4xl lg:text-6xl text-white uppercase tracking-tighter leading-none" data-testid="billing-headline">Unlock The Portal</h1>
          <p className="font-plex text-[#9CA3AF] mt-6 max-w-2xl mx-auto">
            One annual payment unlocks every Level {effectiveLevel} feature. Cancel auto-renewal anytime.{" "}
            <Link to="/pricing" className="text-[#00D4FF] hover:underline" data-testid="billing-switch-plan">Compare plans</Link>
          </p>
        </div>

        <div className="surface p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid="billing-card">
          <div className="lg:col-span-5 border-r-0 lg:border-r border-[#262626] lg:pr-8">
            <div className="section-label mb-3">LEVEL {effectiveLevel} · {plan?.name || "Member"}</div>
            {plan ? (
              <>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="font-chivo font-black text-7xl text-white tracking-tighter">{formatMoney(price ?? 0).replace(".00", "")}</span>
                  <span className="font-mono text-sm text-[#52525B]">/YEAR</span>
                </div>
                {founding ? (
                  <div className="font-mono text-[11px] text-[#00D4FF] tracking-widest uppercase mb-6 flex items-center gap-1">
                    <Fire size={10} weight="fill" /> FOUNDING · {plan.founding_remaining}/{plan.founding_slots} SPOTS LEFT
                  </div>
                ) : (
                  <div className="font-mono text-xs text-[#00D4FF] tracking-widest uppercase mb-6">Annual Subscription</div>
                )}
                <button onClick={checkout} disabled={loading || !plan} className="btn-primary w-full" data-testid="checkout-cta">
                  <CreditCard size={16} weight="bold" /> {loading ? "Loading checkout..." : "Continue to Stripe"}
                </button>
                {error && <div className="text-sm font-mono text-[#EF4444] mt-3" data-testid="billing-error">{error}</div>}
                <p className="font-mono text-[10px] text-[#52525B] mt-4 leading-relaxed">
                  Logged in as <span className="text-[#9CA3AF]">{user?.email}</span>. Payment processed by Stripe.
                </p>
              </>
            ) : (
              <div className="font-mono text-xs text-[#52525B]">[LOADING PLAN]</div>
            )}
          </div>
          <div className="lg:col-span-7">
            <div className="section-label mb-4">INCLUDED</div>
            <ul className="space-y-3">
              {(plan?.features || []).map((f) => (
                <li key={f} className="flex items-start gap-3 font-plex text-sm text-white">
                  <CheckCircle size={18} weight="fill" className="text-[#00D4FF] shrink-0 mt-0.5" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </Layout>
  );
}
